import {HoistModel, LoadSpec, lookup, managed, XH} from '@xh/hoist/core';
import {action, observable, runInAction} from '@xh/hoist/mobx';
import {chain, maxBy, orderBy, random, range, round} from 'lodash';
import {GridModel} from '@xh/hoist/cmp/grid';
import {div, span} from '@xh/hoist/cmp/layout';
import {fmtPrice} from '@xh/hoist/format';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {ChartsTabModel} from '../../ChartsTabModel';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';
import {FullQuote} from '../../../../data/DataTypes';
import {GridOptions} from 'ag-grid-community';

export type Level2Quote = {
    exchange: string;
    price: number;
    size: number;
    color?: null | 'green' | 'red';
    tick?: number;
};

export class Level2WidgetModel extends HoistModel {
    @managed gridModel = this.createGridModel();
    @observable accessor isTickerLinked = false;
    @observable accessor ticker: string;
    @lookup(DashViewModel) dashViewModel: DashViewModel;
    @lookup(() => ChartsTabModel) chartTabModel: ChartsTabModel;
    private rowCount = 0;
    private basePrice = 0;
    private sampleSize = 100;
    private maxBidSize = 0;
    private maxAskSize = 0;
    private priceRange: number;
    private priceIncrement: number;

    private updateIntervalMs = 300;
    private bidBook: Level2Quote[] = [];
    private askBook: Level2Quote[] = [];
    private tick: number = 0;
    private timeoutHandle: NodeJS.Timeout;

    constructor() {
        super();

        this.rowCount = this.exchangeCodes.length;
    }

    override onLinked() {
        this.markPersist('isTickerLinked', {dashViewModel: this.dashViewModel});
        this.markPersist('ticker', {dashViewModel: this.dashViewModel});

        this.addReaction({
            track: () => this.chartTabModel.mainChartTicker,
            run: ticker => {
                if (!ticker) return;
                if (!this.isTickerLinked) return;

                this.setTicker(ticker);
            },
            fireImmediately: true
        });

        this.addReaction({
            track: () => this.isTickerLinked,
            run: isTickerLinked => {
                if (isTickerLinked) {
                    this.ticker = this.chartTabModel.mainChartTicker;
                }
            }
            //fireImmediately: true
        });

        this.addReaction({
            track: () => this.ticker,
            run: () => {
                this.loadAsync();
            }
        });

        runInAction(() => {
            this.ticker = this.dashViewModel.viewState?.ticker;
            this.isTickerLinked = this.dashViewModel.viewState?.isTickerLinked;
        });
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        clearTimeout(this.timeoutHandle);
        this.gridModel.clear();
        this.bidBook = [];
        this.askBook = [];
        this.tick = 0;

        if (!this.ticker) {
            return;
        }

        const quote = await this.fetchPriceAsync();
        this.basePrice = (quote.bid + quote.ask) / 2;
        this.priceIncrement = this.basePrice * 0.0002;
        this.priceRange = (this.priceIncrement * this.rowCount) / 2;

        let price = this.basePrice + this.priceRange;
        range(0, this.rowCount - 1).forEach(idx => {
            price = round(price - random(0, this.priceIncrement, true), 2);
            this.bidBook.push({
                exchange: this.exchangeCodes[idx],
                price,
                size: 1
            });
        });

        price = this.basePrice - this.priceRange;
        range(0, this.rowCount - 1).forEach(idx => {
            price = round(price + random(0, this.priceIncrement, true), 2);
            this.askBook.push({
                exchange: this.exchangeCodes[random(0, this.exchangeCodes.length - 1)],
                price,
                size: 1
            });
        });

        this.updateGrid();
        this.updateSizes();

        await this.updateAsync();
    }

    @action
    setTicker(ticker: string) {
        this.ticker = ticker;
    }

    @action
    toggleLinked() {
        this.isTickerLinked = !this.isTickerLinked;
    }

    get agOptions(): GridOptions {
        return {
            pagination: true,
            suppressPaginationPanel: true,
            paginationPageSize: 0,
            paginationAutoPageSize: true
        };
    }

    private async updateAsync() {
        this.tick++;
        this.updatePrices();
        this.updateSizes();
        this.updateExecutions();
        this.updateGrid();

        this.timeoutHandle = setTimeout(() => this.updateAsync(), this.updateIntervalMs);
    }

    private async fetchPriceAsync(): Promise<FullQuote> {
        const quote: FullQuote = await XH.fetchService
            .getJson({
                url: `${XH.getConf('svcApiUrl')}/quote/fullquote/${this.ticker}`,
                ...getDefaultOptions()
            })
            .catchDefault({alertType: 'toast'});
        return quote;
    }

    private updateGrid() {
        const data = [];
        range(0, this.rowCount - 1).forEach(idx => {
            data.push({
                id: idx,
                bidExchange: this.bidBook[idx].exchange,
                bidPrice: this.bidBook[idx].price,
                bidSize: this.bidBook[idx].size,
                bidIndicator: this.bidBook[idx].color,
                askExchange: this.askBook[idx].exchange,
                askPrice: this.askBook[idx].price,
                askSize: this.askBook[idx].size,
                askIndicator: this.askBook[idx].color
            });
        });

        this.gridModel.loadData(data);
    }

    // update prices on some exchanges and re-sort the books
    private updatePrices() {
        let priceUpdateCount = 1; // random(0, this.rowCount / 5);
        for (let i = 0; i < priceUpdateCount; i++) {
            const idx = random(0, this.bidBook.length - 1);
            this.bidBook[idx].price = round(this.basePrice + random(0, this.priceRange, true), 2);
        }

        priceUpdateCount = 1; // or random
        for (let i = 0; i < priceUpdateCount; i++) {
            const idx = random(0, this.askBook.length - 1);
            this.askBook[idx].price = round(this.basePrice - random(0, this.priceRange, true), 2);
        }

        this.bidBook = orderBy(this.bidBook, 'price', 'desc');
        this.askBook = orderBy(this.askBook, 'price', 'desc');
    }

    // generate fake trades and set color indicator based on exec price vs. bid/ask
    private updateExecutions() {
        // reset color after some delay (in ticks)
        for (let i = 0; i < this.bidBook.length; i++) {
            if (this.bidBook[i].color && this.bidBook[i].tick < this.tick - 3) {
                this.bidBook[i].color = null;
            }
        }

        for (let i = 0; i < this.askBook.length; i++) {
            if (this.askBook[i].color && this.askBook[i].tick < this.tick - 3) {
                this.askBook[i].color = null;
            }
        }

        if (random(1, 4) === 1) {
            const execPrice = round(random(this.basePrice - this.priceRange, this.basePrice + this.priceRange, true), 2);
            const bidQuote = this.bidBook[random(0, this.bidBook.length - 1)];
            if (bidQuote) {
                if (bidQuote.price >= execPrice) bidQuote.color = 'green';
                else bidQuote.color = 'red';
                bidQuote.tick = this.tick;
            }
        }

        if (random(1, 4) === 1) {
            const execPrice = round(random(this.basePrice - this.priceRange, this.basePrice + this.priceRange, true), 2);
            const askQuote = this.askBook[random(0, this.askBook.length - 1)];
            if (askQuote) {
                if (askQuote.price >= execPrice) askQuote.color = 'green';
                else askQuote.color = 'red';
                askQuote.tick = this.tick;
            }
        }
    }

    // update sizes histogram
    private updateSizes() {
        if (random(1, 4) > 1) return; // only update sizes occasionally

        let sizes = this.getRandomHistogram(this.sampleSize, this.rowCount);
        for (let i = 0; i < this.bidBook.length; i++) {
            this.bidBook[i].size = sizes.length > i ? sizes[i] : 1;
        }
        sizes = this.getRandomHistogram(this.sampleSize, this.rowCount);
        for (let i = 0; i < this.askBook.length; i++) {
            this.askBook[i].size = sizes.length > i ? sizes[i] : 1;
        }

        this.maxBidSize = maxBy(this.bidBook, 'size').size;
        this.maxAskSize = maxBy(this.askBook, 'size').size;
    }

    private createGridModel(): GridModel {
        return new GridModel({
            colDefaults: {sortable: false, movable: false, resizable: true},
            contextMenu: null,
            autosizeOptions: {mode: 'managed'},
            stripeRows: false,
            selModel: 'disabled',
            showHover: true,
            // Best bid and ask - the inside quote - stands out from the rest of the book.
            rowClassFn: record => (record?.id === 0 ? 'level2-widget__inside' : null),
            columns: [
                {field: 'id', hidden: true},
                {
                    field: 'bidExchange',
                    headerName: 'EX',
                    align: 'left',
                    flex: 1,
                    rendererIsComplex: true,
                    renderer: (v, {record}) =>
                        div(
                            div({
                                className: 'level2-widget__exchange-bg level2-widget__exchange-bg--bid',
                                style: {
                                    width: `${(100 * record.data.bidSize) / this.maxBidSize}%`
                                }
                            }),
                            span({item: v, className: 'level2-widget__exchange-name__left'})
                        )
                },
                {
                    field: 'bidSize',
                    headerName: 'BS',
                    align: 'right',
                    cellClassRules: {
                        'level2-widget__cell__green': ({data: record}) => record.data.bidIndicator === 'green',
                        'level2-widget__cell__red': ({data: record}) => record.data.bidIndicator === 'red'
                    }
                },
                {
                    field: 'bidPrice',
                    headerName: 'BID ' + '\u25BC',
                    cellClass: 'level2-widget__bid',
                    align: 'right',
                    renderer: v => fmtPrice(v, {precision: 2}),
                    cellClassRules: {
                        'level2-widget__cell__green': ({data: record}) => record.data.bidIndicator === 'green',
                        'level2-widget__cell__red': ({data: record}) => record.data.bidIndicator === 'red'
                    }
                },
                {
                    field: 'bidIndicator',
                    hidden: true
                },
                {
                    field: 'askPrice',
                    headerName: 'ASK ' + '\u25B2',
                    cellClass: 'level2-widget__ask',
                    align: 'right',
                    renderer: v => fmtPrice(v, {precision: 2}),
                    cellClassRules: {
                        'level2-widget__cell__green': ({data: record}) => record.data.askIndicator === 'green',
                        'level2-widget__cell__red': ({data: record}) => record.data.askIndicator === 'red'
                    }
                },
                {
                    field: 'askSize',
                    headerName: 'AS',
                    align: 'right',
                    cellClassRules: {
                        'level2-widget__cell__green': ({data: record}) => record.data.askIndicator === 'green',
                        'level2-widget__cell__red': ({data: record}) => record.data.askIndicator === 'red'
                    }
                },
                {
                    field: 'askExchange',
                    headerName: 'EX',
                    align: 'right',
                    flex: 1,
                    rendererIsComplex: true,
                    renderer: (v, {record}) =>
                        div(
                            div({
                                className: 'level2-widget__exchange-bg level2-widget__exchange-bg--ask',
                                style: {
                                    width: `${(100 * record.data.askSize) / this.maxAskSize}%`,
                                    right: 0
                                }
                            }),
                            span({item: v, className: 'level2-widget__exchange-name__right'})
                        )
                },
                {
                    field: 'askIndicator',
                    hidden: true
                }
            ]
        });
    }

    private getRandomHistogram(sampleSize: number, binCount): number[] {
        const distro = range(1, sampleSize)
            .map(i => this.randomGaussian(1, sampleSize))
            .map(v => round(v, 0));
        const histo = this.histogram(distro, Math.ceil(sampleSize / binCount));
        return histo;
    }

    private randomGaussian(min, max): number {
        let u = 0,
            v = 0;
        while (u === 0) u = Math.random();
        while (v === 0) v = Math.random();
        let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);

        num = num / 10.0 + 0.5;
        if (num > 1 || num < 0) num = this.randomGaussian(min, max);
        else {
            num *= max - min;
            num += min;
        }
        return num;
    }

    private histogram(data: number[], binSize: number): number[] {
        const histogram = chain(data)
            .groupBy(num => Math.floor(num / binSize) * binSize)
            .map((values, binStart) => ({
                bin: `${binStart}-${parseInt(binStart) + binSize - 1}`,
                count: values.length,
                values: values
            }))
            .sortBy('bin')
            .value();

        return histogram.map(h => h.count);
    }

    private exchangeCodes = [
        'NYSE',
        'NASDAQ',
        'AMEX',
        'ARCA',
        'BATS',
        'IEXG',
        'CBOE',
        'AMEX',
        'PHLX',
        'BOX',
        'MIAX',
        'BEX',
        'NSDQ',
        'XASE',
        'XNGS',
        'ARCX',
        'NYSEN',
        'XNYS',
        'OTC',
        'TSX',
        'LSE',
        'JPX',
        'SSE',
        '24EQ',
        'MRPL',
        'XISE',
        'EDGA',
        'EDGX',
        'LTSE',
        'XCHI',
        'XNYS',
        'ARCX',
        'XNAS',
        'MEMX',
        'IEXG',
        'CBSX',
        'XPSX',
        'BATY',
        'BATS'
    ];
}
